"use client";

import Link from "next/link";
import { useState, useTransition } from "react";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import type { ProfileFieldId } from "@/lib/profile/field-config";
import { getFieldCopy } from "@/lib/profile/field-labels";
import { INDUSTRIES, WORK_INTERESTS } from "@/lib/profile/industries";
import { saveProfileField } from "@/server/actions/profile-nudge";

const INTEREST_LABEL_KEYS: Record<string, string> = {
	frontend: "interestFrontend",
	backend: "interestBackend",
	fullstack: "interestFullstack",
	mobile: "interestMobile",
	devops: "interestDevops",
	"data-eng": "interestDataEng",
	"data-science": "interestDataScience",
	"ml-ai": "interestMlAi",
	design: "interestDesign",
	product: "interestProduct",
	marketing: "interestMarketing",
	sales: "interestSales",
	"customer-success": "interestCustomerSuccess",
	writing: "interestWriting",
	ops: "interestOps",
	qa: "interestQa",
	security: "interestSecurity",
};

const INTEREST_LABEL_FALLBACK: Record<string, string> = {
	frontend: "Frontend",
	backend: "Backend",
	fullstack: "Full-stack",
	mobile: "Mobile",
	devops: "DevOps",
	"data-eng": "Data eng",
	"data-science": "Data sci",
	"ml-ai": "ML / AI",
	design: "Design",
	product: "Product",
	marketing: "Marketing",
	sales: "Sales",
	"customer-success": "CS",
	writing: "Writing",
	ops: "Ops",
	qa: "QA",
	security: "Security",
};

function interestLabel(slug: string, t?: Dictionary): string {
	const key = INTEREST_LABEL_KEYS[slug];
	return (key ? t?.[key] : undefined) ?? INTEREST_LABEL_FALLBACK[slug] ?? slug;
}

type FocusRef = React.MutableRefObject<{ focus: () => void } | null>;

type Props = {
	field: ProfileFieldId;
	/** Called once the field has been saved successfully. */
	onSaved: () => void;
	/** Optional ref into the first focusable element (for autofocus on open). */
	firstFocusRef?: FocusRef;
	/** Profile dictionary for localized labels and placeholders. */
	t?: Dictionary;
};

/**
 * Shared form body for the progressive-profile nudge — the input + Save
 * button + inline error message for one field. Wrapped in dialog chrome
 * by the navbar popover and in bottom-sheet chrome by the post-save sheet.
 */
export function ProfileNudgeForm({ field, onSaved, firstFocusRef, t }: Props) {
	const [, startTransition] = useTransition();
	const [error, setError] = useState<string | null>(null);
	const [pending, setPending] = useState(false);

	const handleSubmit = (rawValue: unknown) => {
		setError(null);
		setPending(true);
		startTransition(async () => {
			const r = await saveProfileField(field, rawValue);
			setPending(false);
			if (r.ok) {
				onSaved();
			} else {
				setError(r.error);
			}
		});
	};

	return (
		<>
			<FieldInput
				field={field}
				firstFocusRef={firstFocusRef}
				onSubmit={handleSubmit}
				pending={pending}
				t={t}
			/>
			{error ? (
				<p className="mt-2 text-xs text-destructive" aria-live="polite">
					{error}
				</p>
			) : null}
		</>
	);
}

function FieldInput({
	field,
	firstFocusRef,
	onSubmit,
	pending,
	t,
}: {
	field: ProfileFieldId;
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	t?: Dictionary;
}) {
	const copy = getFieldCopy(field, t);
	const saveLabel = t?.save ?? "Save";
	if (field === "industry") {
		return (
			<IndustryInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				ariaLabel={copy.formLabel}
				pickOneLabel={t?.pickOne ?? "Pick one…"}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "interests") {
		return (
			<InterestsInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				t={t}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "experience") {
		return (
			<ExperienceInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				ariaLabel={copy.formLabel}
				placeholder={t?.placeholderExperience ?? "e.g. 5"}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "company") {
		return (
			<TextInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				placeholder={t?.placeholderCompany ?? "e.g. Acme Corp"}
				inputMode="text"
				maxLength={80}
				ariaLabel={copy.formLabel}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "aspiration") {
		return (
			<AspirationInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				ariaLabel={copy.formLabel}
				placeholder={
					t?.placeholderAspirationShort ??
					"e.g. transition into AI engineering in 12 months"
				}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "linkedin") {
		return (
			<TextInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				placeholder={
					t?.placeholderLinkedin ?? "https://linkedin.com/in/yourname"
				}
				inputMode="url"
				maxLength={500}
				ariaLabel={copy.formLabel}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "github") {
		return (
			<TextInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				placeholder={t?.placeholderGithub ?? "https://github.com/yourhandle"}
				inputMode="url"
				maxLength={500}
				ariaLabel={copy.formLabel}
				saveLabel={saveLabel}
			/>
		);
	}
	if (field === "portfolio") {
		return (
			<TextInput
				firstFocusRef={firstFocusRef}
				onSubmit={onSubmit}
				pending={pending}
				placeholder={t?.placeholderPortfolio ?? "https://yourname.dev"}
				inputMode="url"
				maxLength={500}
				ariaLabel={copy.formLabel}
				saveLabel={saveLabel}
			/>
		);
	}
	// Roles: no controlled list yet — fall back to deep-link to settings.
	return (
		<Link
			href="/settings"
			className="block w-full rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
		>
			{t?.openSettings ?? "Open settings"}
		</Link>
	);
}

function IndustryInput({
	firstFocusRef,
	onSubmit,
	pending,
	ariaLabel,
	pickOneLabel,
	saveLabel,
}: {
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	ariaLabel: string;
	pickOneLabel: string;
	saveLabel: string;
}) {
	const [value, setValue] = useState("");
	return (
		<div className="flex flex-col gap-2">
			<select
				ref={(el) => {
					if (firstFocusRef) firstFocusRef.current = el;
				}}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				aria-label={ariaLabel}
			>
				<option value="">{pickOneLabel}</option>
				{INDUSTRIES.map((i) => (
					<option key={i.slug} value={i.slug}>
						{i.label}
					</option>
				))}
			</select>
			<SubmitButton
				disabled={!value}
				pending={pending}
				onClick={() => onSubmit(value)}
				label={saveLabel}
			/>
		</div>
	);
}

function InterestsInput({
	firstFocusRef,
	onSubmit,
	pending,
	t,
	saveLabel,
}: {
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	t?: Dictionary;
	saveLabel: string;
}) {
	const [picked, setPicked] = useState<string[]>([]);
	const toggle = (slug: string) =>
		setPicked((cur) =>
			cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug],
		);
	const counter = (t?.selectedOfMax ?? "{n}/{max} selected")
		.replace("{n}", String(picked.length))
		.replace("{max}", "5");
	return (
		<div className="flex flex-col gap-2">
			<ul className="flex flex-wrap gap-1.5">
				{WORK_INTERESTS.map((slug, idx) => {
					const active = picked.includes(slug);
					return (
						<li key={slug}>
							<button
								type="button"
								ref={
									idx === 0
										? (el) => {
												if (firstFocusRef) firstFocusRef.current = el;
											}
										: undefined
								}
								onClick={() => toggle(slug)}
								aria-pressed={active}
								className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
									active
										? "border-primary bg-primary/10 text-primary"
										: "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground"
								}`}
							>
								{interestLabel(slug, t)}
							</button>
						</li>
					);
				})}
			</ul>
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs text-muted-foreground">{counter}</span>
				<SubmitButton
					disabled={picked.length === 0 || picked.length > 5}
					pending={pending}
					onClick={() => onSubmit(picked)}
					label={saveLabel}
				/>
			</div>
		</div>
	);
}

function ExperienceInput({
	firstFocusRef,
	onSubmit,
	pending,
	ariaLabel,
	placeholder,
	saveLabel,
}: {
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	ariaLabel: string;
	placeholder: string;
	saveLabel: string;
}) {
	const [value, setValue] = useState("");
	const num = Number(value);
	const valid =
		value !== "" &&
		Number.isFinite(num) &&
		Number.isInteger(num) &&
		num >= 0 &&
		num <= 60;
	return (
		<div className="flex gap-2">
			<input
				ref={(el) => {
					if (firstFocusRef) firstFocusRef.current = el;
				}}
				type="number"
				min={0}
				max={60}
				step={1}
				inputMode="numeric"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder={placeholder}
				className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				aria-label={ariaLabel}
			/>
			<SubmitButton
				disabled={!valid}
				pending={pending}
				onClick={() => onSubmit(num)}
				label={saveLabel}
			/>
		</div>
	);
}

function TextInput({
	firstFocusRef,
	onSubmit,
	pending,
	placeholder,
	inputMode,
	maxLength,
	ariaLabel,
	saveLabel,
}: {
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	placeholder: string;
	inputMode: "text" | "url";
	maxLength: number;
	ariaLabel: string;
	saveLabel: string;
}) {
	const [value, setValue] = useState("");
	const trimmed = value.trim();
	return (
		<div className="flex gap-2">
			<input
				ref={(el) => {
					if (firstFocusRef) firstFocusRef.current = el;
				}}
				type={inputMode === "url" ? "url" : "text"}
				inputMode={inputMode}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder={placeholder}
				maxLength={maxLength}
				aria-label={ariaLabel}
				onKeyDown={(e) => {
					if (e.key === "Enter" && trimmed) onSubmit(value);
				}}
				className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			/>
			<SubmitButton
				disabled={trimmed.length === 0}
				pending={pending}
				onClick={() => onSubmit(value)}
				label={saveLabel}
			/>
		</div>
	);
}

function AspirationInput({
	firstFocusRef,
	onSubmit,
	pending,
	ariaLabel,
	placeholder,
	saveLabel,
}: {
	firstFocusRef?: FocusRef;
	onSubmit: (value: unknown) => void;
	pending: boolean;
	ariaLabel: string;
	placeholder: string;
	saveLabel: string;
}) {
	const [value, setValue] = useState("");
	return (
		<div className="flex flex-col gap-2">
			<textarea
				ref={(el) => {
					if (firstFocusRef) firstFocusRef.current = el;
				}}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder={placeholder}
				maxLength={240}
				rows={3}
				className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				aria-label={ariaLabel}
			/>
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs text-muted-foreground">
					{value.length}/240
				</span>
				<SubmitButton
					disabled={value.trim().length < 10}
					pending={pending}
					onClick={() => onSubmit(value)}
					label={saveLabel}
				/>
			</div>
		</div>
	);
}

function SubmitButton({
	disabled,
	pending,
	onClick,
	label,
}: {
	disabled: boolean;
	pending: boolean;
	onClick: () => void;
	label: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled || pending}
			className="shrink-0 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
		>
			{pending ? "…" : label}
		</button>
	);
}
